import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  ExternalLink, AlertTriangle, Phone, Zap, Wifi, ShoppingBag, 
  Lightbulb, Droplets, Sun, Wind, BookOpen, Home, Car
} from 'lucide-react';
import { getSaveaPhone, getSaveaPhoneTel } from '../config/phoneConfig';
import { SEOHead } from '../components/SEOHead';

export const ProductsPage = () => {
  const { i18n } = useTranslation();
  const saveaPhone = getSaveaPhone('FR', 'electricity', 'LP');
  
  const products = [
    {
      name: 'Tapo P100 (prise connectée)',
      description: 'Prise connectée WiFi intelligente pour contrôler vos appareils à distance et programmer des plages horaires.',
      category: '⚡ Énergie',
      price: '~10-15€',
      link: 'https://www.amazon.fr/dp/B089B6ZJ22',
      savings: 'Économie: 30-50€/an'
    },
    {
      name: 'Disjoncteur/relai WiFi Tuya (rail DIN)',
      description: 'Disjoncteur intelligent pour chauffe-eau. Installation électricien puis pilotage smartphone.',
      category: '⚡ Énergie',
      price: '~25-40€',
      link: 'https://www.amazon.fr/dp/B0CCCZTHML',
      savings: 'Économie: 40-60€/an'
    },
    {
      name: 'Ventilateur sur pied Amazon Basics (télécommande)',
      description: 'Ventilateur silencieux avec télécommande pour rafraîchir efficacement sans climatisation.',
      category: '🏠 Maison',
      price: '~40-60€',
      link: 'https://www.amazon.fr/dp/B07VZRHM6T',
      savings: 'Alternative clim'
    },
    {
      name: 'Réducteur de débit douche Flintronic (x2)',
      description: 'Réduit la consommation d\'eau de 40-60% sans perte de confort. Lot de 2 pièces.',
      category: '💧 Eau',
      price: '~8-12€',
      link: 'https://www.amazon.fr/dp/B0BB1K74JZ',
      savings: 'Économie: 80-150€/an'
    },
    {
      name: 'Mousseur/aérateur de robinet réglable 360°',
      description: 'Embout robinet qui réduit le débit tout en maintenant la pression. Orientable.',
      category: '💧 Eau',
      price: '~5-10€',
      link: 'https://www.amazon.fr/dp/B096MMYKX3',
      savings: 'Économie: 30-60€/an'
    },
    {
      name: 'BRITA On Tap V (système filtre robinet)',
      description: 'Filtre eau robinet pour éliminer chlore, calcaire et impuretés. Alternative bouteilles plastique.',
      category: '💧 Eau',
      price: '~40-50€',
      link: 'https://www.amazon.fr/dp/B0C7CVVQ1P',
      savings: 'Économie bouteilles'
    },
    {
      name: 'BRITA On Tap V-MF (cartouches x2)',
      description: 'Cartouches de rechange pour système BRITA On Tap. Pack de 2 filtres longue durée.',
      category: '💧 Eau',
      price: '~25-35€',
      link: 'https://www.amazon.fr/dp/B0B9NRNN4R',
      savings: 'Recharge système'
    },
    {
      name: 'Kit solaire autoinstallable 555 W (Lumisolar)',
      description: 'Kit panneau solaire plug & play. Branchez et produisez votre électricité. Installation facile.',
      category: '☀️ Solaire',
      price: '~400-600€',
      link: 'https://lumisolar.es/kit-solar-autoinstalable-555w',
      savings: 'Production: 500-700 kWh/an'
    },
    {
      name: 'NOCO Boost GB40 (booster batterie)',
      description: 'Démarreur de batterie portable 1000A. Indispensable en cas de panne. Compact et puissant.',
      category: '🚗 Auto',
      price: '~70-90€',
      link: 'https://www.amazon.fr/dp/B015TKPT1A',
      savings: 'Évite dépannage coûteux'
    },
    {
      name: 'Roborock Q Revo (aspirateur-laveur + station)',
      description: 'Robot aspirateur-laveur haut de gamme avec station de vidage automatique. Testé et approuvé SAVEA.',
      category: '🏠 Maison',
      price: '~500-700€',
      link: 'https://www.amazon.fr/dp/B0C2YJYN8H',
      savings: 'Gain de temps considérable'
    }
  ];

  const usefulLinks = [
    { url: 'https://www.lettre-resiliation.com/', title: 'Lettres de résiliation gratuites', category: 'Administratif' },
    { url: 'https://www.bouyguestelecom.fr/choisir-bouygues-telecom/don-de-giga', title: 'Don de Giga Bouygues', category: 'Télécom' },
    { url: 'https://esim.holafly.com/fr/', title: 'Holafly eSIM voyage', category: 'Télécom' },
    { url: 'https://mobile.free.fr/fiche-forfait-2-euros', title: 'Forfait Free 2€', category: 'Télécom' },
    { url: 'https://www.revolut.com/fr-FR', title: 'Revolut (néobanque)', category: 'Banque' },
    { url: 'https://n26.com/fr-fr', title: 'N26 (néobanque)', category: 'Banque' },
    { url: 'https://www.bioethanolcarburant.com/trouver-une-station-e85/', title: 'Stations E85 France', category: 'Carburant' },
    { url: 'https://www.prix-carburants.gouv.fr/', title: 'Prix carburants officiels', category: 'Carburant' },
    { url: 'https://apps.apple.com/es/app/essence-co/id459724569', title: 'Essence&Co (app prix)', category: 'Carburant' },
    { url: 'https://apps.apple.com/us/app/gasbuddy-find-pay-for-gas/id406719683', title: 'GasBuddy (app prix)', category: 'Carburant' },
    { url: 'https://fr.getaround.com/', title: 'Getaround (location voiture)', category: 'Transport' },
    { url: 'https://www.sncf-voyageurs.com/fr/voyagez-avec-nous/en-france/tarifs-grandes-lignes/carte-avantage/carte-avantage-adulte/', title: 'Carte Avantage SNCF', category: 'Transport' },
    { url: 'https://www.sncf-connect.com/aide/retard-de-votre-train-et-remboursement', title: 'Remboursement retard train', category: 'Transport' },
    { url: 'https://www.thetrainline.com/fr', title: 'Thetrainline (billets train)', category: 'Transport' },
    { url: 'https://www.backmarket.fr/fr-fr/quality/smartphones', title: 'BackMarket (reconditionné)', category: 'Shopping' },
    { url: 'https://www.amazon.fr/', title: 'Amazon France', category: 'Shopping' },
    { url: 'https://www.decathlon.fr/produits-reconditionnes', title: 'Decathlon Reconditionné', category: 'Shopping' },
    { url: 'https://www.leboncoin.fr/', title: 'Leboncoin', category: 'Shopping' },
    { url: 'https://www.livrenpoche.com/', title: 'Livres d\'occasion', category: 'Shopping' },
    { url: 'https://emmaus-france.org/acheter/', title: 'Emmaüs boutiques', category: 'Shopping' },
    { url: 'https://www.croix-rouge.fr/annuaire?categories=boutique-solidaire', title: 'Croix-Rouge boutiques', category: 'Shopping' },
    { url: 'https://www.vinted.fr/', title: 'Vinted', category: 'Shopping' },
    { url: 'https://www.quechoisir.org/carte-interactive-drives-n21243/', title: 'Comparateur drives (UFC)', category: 'Courses' },
    { url: 'https://chromewebstore.google.com/detail/honey-automatic-coupons-r/bmnlcjabgnpnenekpadlanbbkooimhnj', title: 'Extension Honey', category: 'Codes promo' },
    { url: 'https://keepa.com/#!search', title: 'Keepa (historique prix)', category: 'Codes promo' },
    { url: 'https://chromewebstore.google.com/detail/keepa-amazon-price-tracke/neebplgakaahbhdphmkckjjcegoiijjo', title: 'Extension Keepa', category: 'Codes promo' },
    { url: 'https://fr.camelcamelcamel.com/', title: 'CamelCamelCamel', category: 'Codes promo' },
    { url: 'https://www.spliiit.com', title: 'Spliiit (partage abonnements)', category: 'Streaming' },
    { url: 'https://boutique.canalplus.com/produits/offre-canal-plus-cine-series', title: 'Canal+ Ciné Séries', category: 'Streaming' },
    { url: 'https://park4night.com/fr', title: 'Park4Night (camping-car)', category: 'Voyages' },
    { url: 'https://www.joko.com/fr', title: 'Joko (cashback)', category: 'Cashback' },
    { url: 'https://toogoodtogo.fr/fr', title: 'Too Good To Go', category: 'Anti-gaspi' },
    { url: 'https://www.mesdroitssociaux.gouv.fr/', title: 'Mes Droits Sociaux', category: 'Aides' },
    { url: 'https://www.caf.fr/allocataires/droits-et-prestations', title: 'CAF - Droits et prestations', category: 'Aides' },
    { url: 'https://chequeenergie.gouv.fr/', title: 'Chèque Énergie', category: 'Aides' }
  ];

  const productCategories = [
    {
      id: 'energie',
      title: '⚡ Énergie & Électricité',
      icon: Zap,
      color: 'from-yellow-500 to-orange-500',
      products: products.filter(p => p.category === '⚡ Énergie')
    },
    {
      id: 'eau',
      title: '💧 Eau & Économies',
      icon: Droplets,
      color: 'from-blue-500 to-cyan-500',
      products: products.filter(p => p.category === '💧 Eau')
    },
    {
      id: 'maison',
      title: '🏠 Maison & Confort',
      icon: Home,
      color: 'from-green-500 to-emerald-500',
      products: products.filter(p => p.category === '🏠 Maison')
    },
    {
      id: 'solaire',
      title: '☀️ Solaire & Renouvelable',
      icon: Sun,
      color: 'from-amber-500 to-yellow-500',
      products: products.filter(p => p.category === '☀️ Solaire')
    },
    {
      id: 'auto',
      title: '🚗 Automobile',
      icon: Car,
      color: 'from-red-500 to-rose-500',
      products: products.filter(p => p.category === '🚗 Auto')
    }
  ];

  // Grouper les liens par catégorie
  const linksByCategory = usefulLinks.reduce((acc, link) => {
    if (!acc[link.category]) {
      acc[link.category] = [];
    }
    acc[link.category].push(link);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 via-white to-yellow-50">
      <SEOHead 
        title="Produits Testés et Recommandés SAVEA - Guide d'Achat Économique"
        description="Découvrez notre sélection de produits économiques testés et validés par l'équipe SAVEA pour réduire vos factures d'énergie, internet, eau et plus encore."
      />

      {/* Hero Section */}
      <section className="py-12 lg:py-16 bg-gradient-to-r from-amber-600 to-yellow-600">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <Badge className="bg-white/20 text-white border-white/30 mb-4">
              SÉLECTION SAVEA
            </Badge>
            <h1 className="text-3xl lg:text-5xl font-bold mb-4">
              🛒 Produits Testés et Recommandés SAVEA
            </h1>
            <p className="text-xl lg:text-2xl text-amber-100 max-w-3xl mx-auto mb-6">
              Sélection complète de produits économiques testés par nos équipes
            </p>
            <p className="text-amber-100 max-w-2xl mx-auto">
              Chaque produit a été acheté, testé et validé pour vous aider à économiser sur vos factures
            </p>
          </div>
        </div>
      </section>

      {/* Disclaimer Affiliés */}
      <section className="py-8 bg-blue-50 border-y-2 border-blue-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-start gap-4">
            <AlertTriangle className="w-6 h-6 text-blue-600 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-blue-800 mb-3 text-lg">💼 Transparence Totale - Liens Affiliés</h3>
              <p className="text-blue-700 leading-relaxed mb-3 text-sm lg:text-base">
                Certains produits recommandés contiennent des <strong>liens affiliés Amazon</strong>. SAVEA peut recevoir une commission 
                de quelques pourcentages si vous passez par ces liens, <strong>sans aucun impact sur le prix final que vous payez</strong>. 
                Ces commissions nous aident à financer le temps passé à tester, comparer et valider les produits.
              </p>
              <p className="text-blue-700 mb-3 text-sm lg:text-base">
                <strong>Nous encourageons toujours</strong> à privilégier vos commerces de proximité : quincailleries, jardineries, artisans locaux.
              </p>
              <div className="bg-blue-100 rounded-lg p-3 lg:p-4">
                <h5 className="font-semibold text-blue-800 mb-2 text-sm lg:text-base">🎯 Engagement SAVEA :</h5>
                <p className="text-blue-700 text-xs lg:text-sm">
                  Nous ne recommandons jamais un produit que nous n'avons pas acheté, testé et validé nous-mêmes. 
                  Notre objectif est votre satisfaction et vos économies.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Retour au Guide */}
      <section className="py-4 bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link 
            to="/fr/guide-anti-inflation" 
            className="inline-flex items-center gap-2 text-sage-600 hover:text-sage-700 font-medium"
          >
            <BookOpen className="w-5 h-5" />
            <span className="text-sm lg:text-base">← Retour au Guide Anti-Inflation</span>
          </Link>
        </div>
      </section>

      {/* Produits Testés */}
      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 lg:mb-12">
            <h2 className="text-2xl lg:text-3xl font-bold text-sage-900 mb-3">🛍️ Produits Testés SAVEA</h2>
            <p className="text-sage-600">10 produits achetés, testés et validés par nos équipes</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
            {products.map((product, idx) => (
              <Card key={idx} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-4 lg:p-6">
                  <div className="mb-2">
                    <Badge className="bg-amber-100 text-amber-800 text-xs">{product.category}</Badge>
                  </div>
                  <h3 className="font-bold text-sage-900 text-base lg:text-lg mb-2">{product.name}</h3>
                  <p className="text-sage-700 text-sm mb-3 leading-relaxed">
                    {product.description}
                  </p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-sage-600">Prix:</span>
                      <span className="font-semibold text-sage-900">{product.price}</span>
                    </div>
                    {product.savings && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-2">
                        <p className="text-green-700 text-xs font-medium">{product.savings}</p>
                      </div>
                    )}
                  </div>

                  <a 
                    href={product.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2 w-full bg-blue-600 text-white px-4 py-2.5 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Voir le produit
                  </a>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Sites et Liens Utiles */}
      <section className="py-12 lg:py-16 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="mb-8 lg:mb-12 text-center">
            <h2 className="text-2xl lg:text-3xl font-bold text-sage-900 mb-3">🔗 Sites et Liens Utiles</h2>
            <p className="text-sage-600">Plus de 30 sites et outils recommandés pour économiser au quotidien</p>
          </div>

          <div className="space-y-8">
            {Object.entries(linksByCategory).map(([category, links]) => (
              <div key={category}>
                <h3 className="text-xl font-bold text-sage-900 mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-amber-500 rounded-full"></span>
                  {category}
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
                  {links.map((link, idx) => (
                    <a
                      key={idx}
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-between gap-3 p-3 lg:p-4 bg-white rounded-lg border border-sage-200 hover:border-amber-400 hover:shadow-md transition-all group"
                    >
                      <span className="text-sage-700 text-sm font-medium group-hover:text-amber-600 transition-colors flex-1">
                        {link.title}
                      </span>
                      <ExternalLink className="w-4 h-4 text-sage-400 group-hover:text-amber-500 transition-colors flex-shrink-0" />
                    </a>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final Experts SAVEA */}
      <section className="py-12 lg:py-16 bg-gradient-to-br from-sage-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="bg-gradient-to-r from-sage-600 to-sage-700 border-0 text-white">
            <CardContent className="p-6 lg:p-8 text-center">
              <h3 className="font-bold text-xl lg:text-2xl mb-3 lg:mb-4">💡 Besoin de conseils personnalisés ?</h3>
              <p className="text-sage-100 mb-4 lg:mb-6 text-sm lg:text-base">
                Nos experts SAVEA vous accompagnent gratuitement pour choisir les meilleurs produits selon votre situation
              </p>
              <Button 
                onClick={() => window.open(`tel:${getSaveaPhoneTel('FR', 'electricity', 'LP')}`)}
                size="lg"
                className="w-full lg:w-auto bg-white text-sage-700 hover:bg-sage-50 font-bold px-6 lg:px-8 py-3 lg:py-4"
              >
                <Phone className="w-5 h-5 mr-2" />
                <span className="text-sm lg:text-base">Appeler Expert SAVEA</span>
              </Button>
              <div className="text-sage-200 text-xs lg:text-sm mt-3 lg:mt-4">Service gratuit - Lundi à Samedi 8h-21h</div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};
